import { useParams, Link } from 'react-router';
import { useState, useEffect, useRef } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { getProjectBySlug, getAllProjects, getAllCategories } from '@/services/api';
import type { Project } from '@/data/projects';
import type { Category } from '@/services/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { ImageWithFallback } from '@/app/components/figma/ImageWithFallback';
import { generateProjectUrl } from '@/utils/slug';
import React from 'react';

export function ProjectDetailPage() {
  const { id: slug } = useParams();
  const { language, t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [previousImageIndex, setPreviousImageIndex] = useState(0);
  const [project, setProject] = useState<Project | null>(null);
  const [relatedProjects, setRelatedProjects] = useState<Project[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [mouseX, setMouseX] = useState(0);
  const [isHoveringImage, setIsHoveringImage] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [touchOffset, setTouchOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isSnappingBack, setIsSnappingBack] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right' | null>(null);
  const [previewImageIndex, setPreviewImageIndex] = useState<number | null>(null);
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [isAutoSliding, setIsAutoSliding] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [galleryHeight, setGalleryHeight] = useState<number>(0);
  const [imageDimensions, setImageDimensions] = useState<Record<string, { width: number; height: number }>>({});
  const imageRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);
  const currentImageRef = useRef<HTMLImageElement>(null);
  const preloadImageRef = useRef<HTMLImageElement | null>(null);

  // Toggle to show/hide thumbnail gallery
  const showThumbnailGallery = false;

  // Desktop container calculations
  const TARGET_HEIGHT = 550;
  const CONTAINER_MAX_WIDTH = 1280;
  const HORIZONTAL_PADDING = 64; // 32px left + 32px right (lg:px-8)
  const IMAGE_LEFT_PADDING = 225;
  const AVAILABLE_WIDTH = CONTAINER_MAX_WIDTH - HORIZONTAL_PADDING - IMAGE_LEFT_PADDING; // ~991px
  const TARGET_ASPECT_RATIO = AVAILABLE_WIDTH / TARGET_HEIGHT;

  // Check if image should be cropped on desktop
  const shouldCropOnDesktop = (imageUrl: string): boolean => {
    const dims = imageDimensions[imageUrl];
    if (!dims) return false;
    
    const imageAspectRatio = dims.width / dims.height;
    // If image aspect ratio is wider than container, it will be cropped
    return imageAspectRatio > TARGET_ASPECT_RATIO;
  };

  // Load image dimensions when project loads
  useEffect(() => {
    if (!project) return;
    
    const loadDimensions = async () => {
      const dims: Record<string, { width: number; height: number }> = {};
      
      for (const imageUrl of project.images) {
        const img = new Image();
        img.src = imageUrl;
        
        await new Promise<void>((resolve) => {
          img.onload = () => {
            dims[imageUrl] = { width: img.width, height: img.height };
            resolve();
          };
          img.onerror = () => resolve();
        });
      }
      
      setImageDimensions(dims);
    };
    
    loadDimensions();
  }, [project]);

  // Update gallery height when current image changes or loads
  useEffect(() => {
    if (!currentImageRef.current || window.innerWidth >= 768) return;
    
    const updateHeight = () => {
      if (currentImageRef.current) {
        const height = currentImageRef.current.offsetHeight;
        setGalleryHeight(height);
      }
    };
    
    // Update height when image loads
    const img = currentImageRef.current;
    if (img.complete) {
      updateHeight();
    } else {
      img.addEventListener('load', updateHeight);
    }

    // Update height on window resize
    const handleResize = () => {
      if (window.innerWidth < 768) {
        updateHeight();
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      img.removeEventListener('load', updateHeight);
      window.removeEventListener('resize', handleResize);
    };
  }, [currentImageIndex, project?.images]);

  // Preload next/previous image and update gallery height BEFORE transition starts
  const preloadImageAndUpdateHeight = (nextIndex: number) => {
    if (window.innerWidth >= 768) return; // Only needed on mobile
    
    // Create a temporary image to preload and measure
    if (!preloadImageRef.current) {
      preloadImageRef.current = document.createElement('img');
    }
    
    const tempImg = preloadImageRef.current;
    tempImg.style.position = 'absolute';
    tempImg.style.visibility = 'hidden';
    tempImg.style.width = '100%';
    tempImg.style.height = 'auto';
    
    // Add to DOM temporarily to get accurate measurements
    if (imageRef.current && !tempImg.parentElement) {
      imageRef.current.appendChild(tempImg);
    }
    
    tempImg.onload = () => {
      const height = tempImg.offsetHeight;
      console.log(`Preloaded image ${nextIndex} height:`, height);
      setGalleryHeight(height);
    };
    
    tempImg.src = project.images[nextIndex];
  };

  useEffect(() => {
    async function loadProject() {
      if (!slug) return;
      
      // Scroll to top when project changes
      window.scrollTo(0, 0);
      
      setLoading(true);
      const projectData = await getProjectBySlug(slug);
      console.log('ProjectDetailPage - loaded project:', projectData);
      console.log('ProjectDetailPage - customFields:', projectData?.customFields);
      setProject(projectData);
      
      // Load categories
      const categoriesData = await getAllCategories();
      setCategories(categoriesData);
      
      // Load related projects
      if (projectData) {
        const allProjects = await getAllProjects();
        const related = allProjects.filter(
          p => p.category === projectData.category && p.id !== projectData.id
        ).slice(0, 3);
        setRelatedProjects(related);
      }
      
      setLoading(false);
    }
    loadProject();
  }, [slug]);

  // Keyboard navigation
  useEffect(() => {
    // Disable keyboard navigation if only one image
    if (!project || project.images.length <= 1) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentImageIndex, project?.images.length, isTransitioning, isAnimating]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
      </div>
    );
  }
  
  if (!project) {
    return <div className="min-h-screen flex items-center justify-center">Project not found</div>;
  }

  const nextImage = () => {
    if (isTransitioning || isAnimating) return;
    if (!imageRef.current) return;
    
    // Start animating
    setIsAnimating(true);
    setSwipeDirection('right');
    setPreviousImageIndex(currentImageIndex);
    
    // Set preview image immediately
    setPreviewImageIndex((currentImageIndex + 1) % project.images.length);
    preloadImageAndUpdateHeight((currentImageIndex + 1) % project.images.length);
    
    // Animate from 0 to full width
    const containerWidth = imageRef.current.clientWidth;
    const duration = 400; // milliseconds
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Linear progress - no easing
      const easeProgress = progress;
      
      // Going right (next), offset is positive (swipe left motion)
      const offset = containerWidth * easeProgress;
      setTouchOffset(offset);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete - update index and clean up immediately
        setCurrentImageIndex((prev) => prev === project.images.length - 1 ? 0 : prev + 1);
        
        // Clean up immediately without CSS transition phase
        setIsAnimating(false);
        setSwipeDirection(null);
        setTouchOffset(0);
        setPreviewImageIndex(null);
      }
    };
    
    requestAnimationFrame(animate);
  };

  const prevImage = () => {
    if (isTransitioning || isAnimating) return;
    if (!imageRef.current) return;
    
    // Start animating
    setIsAnimating(true);
    setSwipeDirection('left');
    setPreviousImageIndex(currentImageIndex);
    
    // Set preview image immediately
    setPreviewImageIndex(currentImageIndex === 0 ? project.images.length - 1 : currentImageIndex - 1);
    preloadImageAndUpdateHeight(currentImageIndex === 0 ? project.images.length - 1 : currentImageIndex - 1);
    
    // Animate from 0 to full width
    const containerWidth = imageRef.current.clientWidth;
    const duration = 400; // milliseconds
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Linear progress - no easing
      const easeProgress = progress;
      
      // Going left (prev), offset is negative (swipe right motion)
      const offset = -(containerWidth * easeProgress);
      setTouchOffset(offset);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete - update index and clean up immediately
        setCurrentImageIndex((prev) => prev === 0 ? project.images.length - 1 : prev - 1);
        
        // Clean up immediately without CSS transition phase
        setIsAnimating(false);
        setSwipeDirection(null);
        setTouchOffset(0);
        setPreviewImageIndex(null);
      }
    };
    
    requestAnimationFrame(animate);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    // Disable touch navigation if only one image
    if (!project || project.images.length <= 1) return;
    
    setTouchStart(e.touches[0].clientX);
    setTouchOffset(0);
    setIsSwiping(false);
    setTouchStartTime(Date.now());
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    // Disable touch navigation if only one image
    if (!project || project.images.length <= 1) return;
    if (touchStart === 0) return;
    
    const currentX = e.touches[0].clientX;
    const offset = touchStart - currentX;
    
    setTouchOffset(offset);
    
    if (Math.abs(offset) > 5) {
      setIsSwiping(true);
      setSwipeDirection(offset > 0 ? 'right' : 'left');
      // Set the preview image index when swipe starts
      if (previewImageIndex === null) {
        if (offset > 0) {
          setPreviewImageIndex((currentImageIndex + 1) % project.images.length);
        } else {
          setPreviewImageIndex(currentImageIndex === 0 ? project.images.length - 1 : currentImageIndex - 1);
        }
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!imageRef.current) return;
    
    const finalOffset = touchOffset;
    const containerWidth = imageRef.current.clientWidth;
    const threshold = containerWidth / 2;
    
    // Calculate velocity using the entire swipe duration
    const totalTime = Date.now() - touchStartTime;
    const totalDistance = Math.abs(finalOffset);
    const velocity = totalTime > 0 ? totalDistance / totalTime : 0;
    
    // Velocity threshold: 0.5 px/ms = 500 px/s
    const velocityThreshold = 0.5;
    const hasEnoughVelocity = velocity > velocityThreshold && totalDistance > 10;
    
    console.log('Swipe stats:', { totalDistance, totalTime, velocity, hasEnoughVelocity, threshold });
    
    if (Math.abs(finalOffset) > threshold || hasEnoughVelocity) {
      // Complete the swipe - slide out completely from current position
      setIsTransitioning(true);
      if (finalOffset > 0) {
        setSlideDirection('right');
        setPreviousImageIndex(currentImageIndex);
        setCurrentImageIndex((prev) => prev === project.images.length - 1 ? 0 : prev + 1);
        // Preload next image
        preloadImageAndUpdateHeight((currentImageIndex + 1) % project.images.length);
      } else {
        setSlideDirection('left');
        setPreviousImageIndex(currentImageIndex);
        setCurrentImageIndex((prev) => prev === 0 ? project.images.length - 1 : prev - 1);
        // Preload previous image
        preloadImageAndUpdateHeight(currentImageIndex === 0 ? project.images.length - 1 : currentImageIndex - 1);
      }
      
      setTimeout(() => {
        setIsTransitioning(false);
        setSlideDirection(null);
        setTouchOffset(0);
        setIsSwiping(false);
        setPreviewImageIndex(null);
      }, 300);
    } else {
      // Snap back to original position
      setIsSnappingBack(true);
      // Use setTimeout to trigger animation on next frame
      setTimeout(() => {
        setTouchOffset(0);
      }, 10);
      
      setTimeout(() => {
        setIsSnappingBack(false);
        setIsSwiping(false);
        setSwipeDirection(null);
        setPreviewImageIndex(null);
      }, 310);
    }
    
    setTouchStart(0);
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only allow click navigation on desktop (not during touch)
    // Disable if only one image
    if (isSwiping || isAutoSliding || isAnimating || isTransitioning || window.innerWidth < 768 || !project || project.images.length <= 1) return;
    if (!imageRef.current) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
    
    // Determine direction
    const direction = x < width / 2 ? 'left' : 'right';
    
    // Start animating
    setIsAnimating(true);
    setSwipeDirection(direction);
    setPreviousImageIndex(currentImageIndex);
    
    // Set preview image immediately
    if (direction === 'right') {
      setPreviewImageIndex((currentImageIndex + 1) % project.images.length);
      preloadImageAndUpdateHeight((currentImageIndex + 1) % project.images.length);
    } else {
      setPreviewImageIndex(currentImageIndex === 0 ? project.images.length - 1 : currentImageIndex - 1);
      preloadImageAndUpdateHeight(currentImageIndex === 0 ? project.images.length - 1 : currentImageIndex - 1);
    }
    
    // Animate from 0 to full width
    const containerWidth = imageRef.current.clientWidth;
    const duration = 400; // milliseconds
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Linear progress - no easing
      const easeProgress = progress;
      
      // Offset direction depends on slide direction
      // If going right (next), offset is positive (swipe left motion)
      // If going left (prev), offset is negative (swipe right motion)
      const offset = direction === 'right' 
        ? containerWidth * easeProgress 
        : -(containerWidth * easeProgress);
      setTouchOffset(offset);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete - update index and clean up immediately
        // Update current image index
        if (direction === 'right') {
          setCurrentImageIndex((prev) => prev === project.images.length - 1 ? 0 : prev + 1);
        } else {
          setCurrentImageIndex((prev) => prev === 0 ? project.images.length - 1 : prev - 1);
        }
        
        // Clean up immediately without CSS transition phase
        setIsAnimating(false);
        setSwipeDirection(null);
        setTouchOffset(0);
        setPreviewImageIndex(null);
      }
    };
    
    requestAnimationFrame(animate);
  };

  const startAutoSlide = () => {
    if (isAutoSliding) return;
    setIsAutoSliding(true);
    animationRef.current = window.setInterval(nextImage, 5000);
  };

  const stopAutoSlide = () => {
    if (!isAutoSliding) return;
    setIsAutoSliding(false);
    if (animationRef.current) {
      window.clearInterval(animationRef.current);
      animationRef.current = null;
    }
  };

  // Get category name from categories array
  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(cat => cat.id === categoryId);
    if (category) {
      return category.name[language];
    }
    // Fallback to translation key if category not found
    return t(`category.${categoryId}`);
  };

  return (
    <div className="pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Project Gallery */}
        <div 
          className={`relative bg-white mb-2 md:h-[550px] md:mb-4 overflow-hidden ${project.images.length > 1 ? 'cursor-pointer' : ''}`}
          style={{ 
            height: window.innerWidth < 768 && galleryHeight > 0 ? `${galleryHeight}px` : undefined,
            touchAction: 'pan-y',
            isolation: 'isolate',
            transition: window.innerWidth < 768 ? 'height 0.3s ease-out' : undefined
          }}
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = e.clientX - rect.left;
            setMouseX(x);
            setIsHoveringImage(true);
          }}
          onMouseLeave={() => {
            setIsHoveringImage(false);
          }}
          onClick={handleClick}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
          ref={imageRef}
        >
          {/* Previous image (old current) - slides out during transition from current position */}
          {isTransitioning && slideDirection && imageRef.current && (
            <div 
              className="absolute inset-0 w-full h-full md:pl-[225px]"
              style={{
                zIndex: -1,
                transform: slideDirection === 'right' 
                  ? `translateX(${-(imageRef.current.clientWidth + 16)}px)` 
                  : `translateX(${imageRef.current.clientWidth + 16}px)`,
                transition: 'transform 0.15s linear',
                willChange: 'transform'
              }}
            >
              {/* Image counter - desktop only, absolute left */}
              <div className="hidden md:block absolute bottom-0 left-0 text-black text-lg font-light leading-none">
                {previousImageIndex + 1}/{project.images.length}
              </div>
              <div className="relative w-full h-full md:flex md:items-end md:justify-end">
                <div className="relative w-full md:w-auto">
                  <img
                    src={project.images[previousImageIndex]}
                    alt={`${project.title[language]} - Previous`}
                    className={`w-full md:w-auto md:h-[550px] object-[center_bottom] ${shouldCropOnDesktop(project.images[previousImageIndex]) ? 'object-cover' : 'object-contain'}`}
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Current image - follows touch or stays in place */}
          <div 
            className="relative md:absolute md:inset-0 w-full md:h-full md:pl-[225px]"
            style={{
              zIndex: -1,
              ...(isSwiping || isSnappingBack || isAnimating || isTransitioning) ? {
                transform: isTransitioning 
                  ? slideDirection === 'right'
                    ? `translateX(${-(imageRef.current?.clientWidth || 0) - 16}px)` 
                    : `translateX(${(imageRef.current?.clientWidth || 0) + 16}px)`
                  : `translateX(${-touchOffset}px)`,
                transition: isSnappingBack 
                  ? 'transform 0.3s linear'
                  : isTransitioning 
                    ? 'transform 0.15s linear' 
                    : 'none',
                willChange: 'transform'
              } : {}
            }}
          >
            {/* Image counter - desktop only, absolute left */}
            <div className="hidden md:block absolute bottom-0 left-0 text-black text-lg font-light leading-none">
              {(isTransitioning ? previousImageIndex : currentImageIndex) + 1}/{project.images.length}
            </div>
            <div className="relative w-full md:h-full md:flex md:items-end md:justify-end">
              <div className="relative w-full md:w-auto md:aspect-auto">
                <img
                  ref={currentImageRef}
                  src={project.images[isTransitioning ? previousImageIndex : currentImageIndex]}
                  alt={`${project.title[language]} - Current`}
                  className={`w-full h-auto md:w-auto md:h-[550px] object-[center_bottom] ${shouldCropOnDesktop(project.images[isTransitioning ? previousImageIndex : currentImageIndex]) ? 'object-cover' : 'object-contain'}`}
                />
                
                {/* Navigation Arrows - inside image wrapper */}
                {project.images.length > 1 && isHoveringImage && (
                  <>
                    {/* Left Arrow - show on left half */}
                    {mouseX < (imageRef.current?.clientWidth || 0) / 2 && (
                      <div className="hidden md:block absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        <ChevronLeft 
                          size={96} 
                          className="text-white" 
                          strokeWidth={1}
                          style={{
                            filter: 'drop-shadow(1px 0 0 #888) drop-shadow(-1px 0 0 #888) drop-shadow(0 1px 0 #888) drop-shadow(0 -1px 0 #888)'
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Right Arrow - show on right half */}
                    {mouseX >= (imageRef.current?.clientWidth || 0) / 2 && (
                      <div className="hidden md:block absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        <ChevronRight 
                          size={96} 
                          className="text-white" 
                          strokeWidth={1}
                          style={{
                            filter: 'drop-shadow(1px 0 0 #888) drop-shadow(-1px 0 0 #888) drop-shadow(0 1px 0 #888) drop-shadow(0 -1px 0 #888)'
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Next image - preview during swipe, slides in during transition */}
          {((isSwiping || isSnappingBack || isAnimating) || isTransitioning) && imageRef.current && swipeDirection && (
            <div 
              className="absolute inset-0 w-full h-full md:pl-[225px]"
              style={(() => {
                const baseStyle = { zIndex: -1, willChange: 'transform' };
                if (isTransitioning) {
                  return {
                    ...baseStyle,
                    transform: 'translateX(0)',
                    transition: 'transform 0.15s linear'
                  };
                } else if (isSnappingBack) {
                  // Snap back: move in direction of swipe (back to off-screen)
                  return {
                    ...baseStyle,
                    transform: `translateX(${swipeDirection === 'right' 
                      ? `${imageRef.current.clientWidth + 16}px`
                      : `${-(imageRef.current.clientWidth + 16)}px`
                    })`,
                    transition: 'transform 0.3s linear'
                  };
                } else {
                  // Following touch
                  return {
                    ...baseStyle,
                    transform: `translateX(${touchOffset > 0 
                      ? `${imageRef.current.clientWidth + 16 - touchOffset}px`
                      : `${-(imageRef.current.clientWidth + 16) - touchOffset}px`
                    })`,
                    transition: 'none'
                  };
                }
              })()}
            >
              {/* Image counter - desktop only, absolute left */}
              <div className="hidden md:block absolute bottom-0 left-0 text-black text-lg font-light leading-none">
                {(() => {
                  let nextIndex;
                  if (isTransitioning) {
                    nextIndex = currentImageIndex;
                  } else if (previewImageIndex !== null) {
                    nextIndex = previewImageIndex;
                  } else if (touchOffset > 0) {
                    nextIndex = (currentImageIndex + 1) % project.images.length;
                  } else {
                    nextIndex = currentImageIndex === 0 ? project.images.length - 1 : currentImageIndex - 1;
                  }
                  return `${nextIndex + 1}/${project.images.length}`;
                })()}
              </div>
              <div className="relative w-full md:h-full md:flex md:items-end md:justify-end">
                <div className="relative w-full md:w-auto">
                  <img
                    src={isTransitioning 
                      ? project.images[currentImageIndex]
                      : previewImageIndex !== null
                        ? project.images[previewImageIndex]
                        : touchOffset > 0 
                          ? project.images[(currentImageIndex + 1) % project.images.length]
                          : project.images[currentImageIndex === 0 ? project.images.length - 1 : currentImageIndex - 1]
                    }
                    alt={`${project.title[language]} - Next`}
                    className={`w-full h-auto md:w-auto md:h-[550px] object-[center_bottom] ${shouldCropOnDesktop(
                      isTransitioning 
                        ? project.images[currentImageIndex]
                        : previewImageIndex !== null
                          ? project.images[previewImageIndex]
                          : touchOffset > 0 
                            ? project.images[(currentImageIndex + 1) % project.images.length]
                            : project.images[currentImageIndex === 0 ? project.images.length - 1 : currentImageIndex - 1]
                    ) ? 'object-cover' : 'object-contain'}`}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Image Counter - Mobile only, below image */}
        <div className="md:hidden text-black text-lg font-light leading-none mb-6" style={{ transition: 'transform 0.3s ease-out' }}>
          {currentImageIndex + 1}/{project.images.length}
        </div>

        {/* Thumbnail Gallery */}
        {showThumbnailGallery && project.images.length > 1 && (
          <div className="flex gap-2 md:gap-4 mb-8" style={{ transition: 'transform 0.3s ease-out' }}>
            {project.images.map((image, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`flex-1 aspect-[4/3] overflow-hidden transition-all ${
                  currentImageIndex === index 
                    ? 'ring-2 ring-black' 
                    : 'opacity-60 hover:opacity-100'
                }`}
              >
                <img
                  src={image}
                  alt={`${project.title[language]} - Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}

        {/* Project Title */}
        {(() => {
          const title = project.title[language];
          const city = project.location.split(',')[0].trim();
          const isHungary = project.location.toLowerCase().includes('magyarország') || 
                           project.location.toLowerCase().includes('hungary');
          const location = isHungary ? city : project.location;
          
          // Check if title already contains the city name
          const titleContainsLocation = title.toLowerCase().includes(city.toLowerCase());
          
          if (titleContainsLocation) {
            return (
              <h2 className="text-gray-900 uppercase tracking-wide text-xl mb-8" style={{ fontWeight: 300, fontSize: '1.1rem' }}>
                {title}
              </h2>
            );
          } else {
            return (
              <h2 className="uppercase tracking-wide text-xl mb-8" style={{ fontWeight: 300, fontSize: '1.1rem' }}>
                <span className="text-gray-900">{title}</span>
                {' '}
                <span className="text-gray-400">{location}</span>
              </h2>
            );
          }
        })()}

        {/* Project Details and Description Grid */}
        <div className="grid md:grid-cols-2 gap-12">
          {/* Details - Left side */}
          <div>
            <div className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-1">
              <p className="text-base text-gray-500 text-left">{t('project.category')}</p>
              <p className="text-base text-left">{getCategoryName(project.category)}</p>
              
              {project.client?.[language] && (
                <>
                  <p className="text-base text-gray-500 text-left">{language === 'hu' ? 'Ügyfél' : 'Client'}</p>
                  <p className="text-base text-left">{project.client[language]}</p>
                </>
              )}
              
              {project.location && (
                <>
                  <p className="text-base text-gray-500 text-left">{t('project.location')}</p>
                  <p className="text-base text-left">{project.location}</p>
                </>
              )}
              
              {project.designYear && (
                <>
                  <p className="text-base text-gray-500 text-left">{language === 'hu' ? 'Tervezés éve' : 'Design Year'}</p>
                  <p className="text-base text-left">{project.designYear}</p>
                </>
              )}
              {project.completionYear && (
                <>
                  <p className="text-base text-gray-500 text-left">{language === 'hu' ? 'Megvalósulás éve' : 'Completion Year'}</p>
                  <p className="text-base text-left">{project.completionYear}</p>
                </>
              )}
              
              {project.area && (
                <>
                  <p className="text-base text-gray-500 text-left">{t('project.area')}</p>
                  <p className="text-base text-left">{project.area}</p>
                </>
              )}
              
              {/* Custom Fields */}
              {project.customFields && project.customFields.length > 0 && (
                <>
                  {/* Spacer to create visual separation */}
                  <div className="col-span-2 h-5"></div>
                  {project.customFields.map((field, index) => {
                    // Check if at least one language has both title and content
                    const hasHuData = field.title?.hu && field.content?.hu;
                    const hasEnData = field.title?.en && field.content?.en;
                    
                    if (!hasHuData && !hasEnData) return null;
                    
                    // Use current language if available, otherwise fallback to the other language
                    const title = field.title?.[language] || field.title?.hu || field.title?.en || '';
                    const content = field.content?.[language] || field.content?.hu || field.content?.en || '';
                    
                    if (!title || !content) return null;
                    
                    return (
                      <div key={index} className="contents">
                        <p className="text-base text-gray-500 text-left">{title}</p>
                        <p className="text-base text-left">{content}</p>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>

          {/* Description - Right side */}
          <div>
            <p 
              className="text-gray-700 leading-relaxed text-justify" 
              lang={language}
              style={{
                hyphens: 'auto',
                WebkitHyphens: 'auto',
                MsHyphens: 'auto',
                wordWrap: 'break-word'
              }}
            >
              {project.description[language]}
            </p>
          </div>
        </div>

        {/* Related Projects */}
        {relatedProjects.length > 0 && (
          <div className="mt-24">
            <h2 className="mb-12 uppercase tracking-wide" style={{ fontWeight: 300, fontSize: '1.1rem' }}>
              {t('project.relatedProjects')}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-x-8 md:gap-y-8">
              {relatedProjects.map((relatedProject, index) => {
                // Calculate column position for alignment (same as main Projects component)
                const columnIndexMd = index % 2;
                const columnIndexLg = index % 3;
                
                const alignClassMd = columnIndexMd === 0 ? 'md:justify-self-start' : 'md:justify-self-end';
                const alignClassLg = columnIndexLg === 0 ? 'lg:justify-self-start' : 
                                     columnIndexLg === 1 ? 'lg:justify-self-center' : 
                                     'lg:justify-self-end';
                
                return (
                  <Link
                    key={relatedProject.id}
                    to={`/project/${generateProjectUrl(relatedProject)}`}
                    className={`group cursor-pointer justify-self-center ${alignClassMd} ${alignClassLg}`}
                  >
                    <div className="relative w-full aspect-[16/9] md:w-[360px] mb-3 overflow-hidden">
                      <ImageWithFallback
                        src={relatedProject.images[0]}
                        alt={relatedProject.title[language]}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="space-y-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300">
                      {(() => {
                        const title = relatedProject.title[language];
                        const city = relatedProject.location.split(',')[0].trim();
                        const isHungary = relatedProject.location.toLowerCase().includes('magyarország') || 
                                         relatedProject.location.toLowerCase().includes('hungary');
                        const location = isHungary ? city : relatedProject.location;
                        
                        // Check if title already contains the city name
                        const titleContainsLocation = title.toLowerCase().includes(city.toLowerCase());
                        
                        if (titleContainsLocation) {
                          return (
                            <p className="text-gray-900 uppercase tracking-wide leading-tight" style={{ fontWeight: 300, fontSize: '1.1rem' }}>
                              {title}
                            </p>
                          );
                        } else {
                          return (
                            <p className="uppercase tracking-wide leading-tight" style={{ fontWeight: 300, fontSize: '1.1rem' }}>
                              <span className="text-gray-900">{title}</span>
                              {' '}
                              <span className="text-gray-400">{location}</span>
                            </p>
                          );
                        }
                      })()}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}